import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Share, MoreVertical, MoreHorizontal, Plus, ExternalLink, Monitor, Smartphone } from 'lucide-react';
import {
  useDeviceDetection,
  shouldShowA2HSPrompt,
  setA2HSDismissed,
  type InstallMethod,
} from '../hooks/useDeviceDetection';

// ─── Props ───────────────────────────────────────────────────────────────────

interface AddToHomescreenPromptProps {
  /** App name shown in the prompt */
  appName?: string;
  /** App icon URL (square, ideally 80x80+) */
  appIconUrl?: string;
  /** Delay in ms before showing the prompt after mount. Default: 3000 */
  delayMs?: number;
  /** Force show for debugging (ignores dismissed state & standalone check) */
  forceShow?: boolean;
  /** Called when user dismisses the prompt */
  onDismiss?: () => void;
  /** Called when user completes install (desktop native only) */
  onInstall?: () => void;
}

// ─── Animated Step Number ────────────────────────────────────────────────────

const StepNumber: React.FC<{ n: number }> = ({ n }) => (
  <div className="w-7 h-7 rounded-full bg-brand-primary flex items-center justify-center shrink-0 mt-0.5">
    <span className="text-[13px] font-semibold text-white">{n}</span>
  </div>
);

// ─── Inline icon "chip" (mimics OS UI buttons) ──────────────────────────────

const IconChip: React.FC<{ children: React.ReactNode; label?: string }> = ({ children, label }) => (
  <span className="inline-flex items-center gap-1.5 bg-neutral-100 border border-neutral-200/80 rounded-md px-2 py-0.5 mx-0.5 align-middle">
    {children}
    {label && <span className="text-[13px] font-medium text-brand-black/80">{label}</span>}
  </span>
);

// ─── Platform-specific instruction sets ──────────────────────────────────────

const IOSSafariSteps: React.FC = () => (
  <div className="flex flex-col gap-4">
    <div className="flex gap-3 items-start">
      <StepNumber n={1} />
      <p className="text-[15px] text-brand-black/80 leading-relaxed">
        Tocá el botón
        <IconChip>
          <Share className="w-4 h-4 text-brand-primary" />
        </IconChip>
        en la barra inferior.
      </p>
    </div>
    <div className="flex gap-3 items-start">
      <StepNumber n={2} />
      <p className="text-[15px] text-brand-black/80 leading-relaxed">
        Seleccioná
        <IconChip label="Agregar a Inicio">
          <Plus className="w-3.5 h-3.5 text-brand-black/60" />
        </IconChip>
        en el menú.
      </p>
    </div>
    <div className="flex gap-3 items-start">
      <StepNumber n={3} />
      <p className="text-[15px] text-brand-black/80 leading-relaxed">
        Tocá <span className="font-semibold text-brand-black">Agregar</span> en la esquina superior derecha.
      </p>
    </div>
  </div>
);

const IOSSafari26Steps: React.FC = () => (
  <div className="flex flex-col gap-4">
    <div className="flex gap-3 items-start">
      <StepNumber n={1} />
      <p className="text-[15px] text-brand-black/80 leading-relaxed">
        Tocá
        <IconChip>
          <MoreHorizontal className="w-4 h-4 text-brand-black/60" />
        </IconChip>
        en la barra inferior.
      </p>
    </div>
    <div className="flex gap-3 items-start">
      <StepNumber n={2} />
      <p className="text-[15px] text-brand-black/80 leading-relaxed">
        Tocá
        <IconChip>
          <Share className="w-4 h-4 text-brand-primary" />
          <span className="text-[13px] font-medium text-brand-black/80">Compartir</span>
        </IconChip>
      </p>
    </div>
    <div className="flex gap-3 items-start">
      <StepNumber n={3} />
      <p className="text-[15px] text-brand-black/80 leading-relaxed">
        Tocá
        <IconChip>
          <MoreHorizontal className="w-4 h-4 text-brand-black/40" />
          <span className="text-[13px] font-medium text-brand-black/80">Más</span>
        </IconChip>
      </p>
    </div>
    <div className="flex gap-3 items-start">
      <StepNumber n={4} />
      <p className="text-[15px] text-brand-black/80 leading-relaxed">
        Seleccioná
        <IconChip label="Agregar a Inicio">
          <Plus className="w-3.5 h-3.5 text-brand-black/60" />
        </IconChip>
      </p>
    </div>
  </div>
);

const IOSChromeSteps: React.FC = () => (
  <div className="flex flex-col gap-4">
    <div className="flex gap-3 items-start">
      <StepNumber n={1} />
      <p className="text-[15px] text-brand-black/80 leading-relaxed">
        Tocá
        <IconChip>
          <MoreHorizontal className="w-4 h-4 text-brand-black/60" />
        </IconChip>
        en la esquina superior derecha.
      </p>
    </div>
    <div className="flex gap-3 items-start">
      <StepNumber n={2} />
      <p className="text-[15px] text-brand-black/80 leading-relaxed">
        Seleccioná
        <IconChip label="Agregar a Inicio">
          <Plus className="w-3.5 h-3.5 text-brand-black/60" />
        </IconChip>
        del menú.
      </p>
    </div>
  </div>
);

const AndroidChromeSteps: React.FC = () => (
  <div className="flex flex-col gap-4">
    <div className="flex gap-3 items-start">
      <StepNumber n={1} />
      <p className="text-[15px] text-brand-black/80 leading-relaxed">
        Tocá
        <IconChip>
          <MoreVertical className="w-4 h-4 text-brand-black/60" />
        </IconChip>
        en la barra del navegador.
      </p>
    </div>
    <div className="flex gap-3 items-start">
      <StepNumber n={2} />
      <p className="text-[15px] text-brand-black/80 leading-relaxed">
        Seleccioná
        <IconChip label="Agregar a Inicio">
          <Plus className="w-3.5 h-3.5 text-brand-black/60" />
        </IconChip>
      </p>
    </div>
    <div className="flex gap-3 items-start">
      <StepNumber n={3} />
      <p className="text-[15px] text-brand-black/80 leading-relaxed">
        Tocá <span className="font-semibold text-brand-black">Instalar</span> en el diálogo.
      </p>
    </div>
  </div>
);

const AndroidEdgeSteps: React.FC = () => (
  <div className="flex flex-col gap-4">
    <div className="flex gap-3 items-start">
      <StepNumber n={1} />
      <p className="text-[15px] text-brand-black/80 leading-relaxed">
        Tocá
        <IconChip>
          <MoreHorizontal className="w-4 h-4 text-brand-black/60" />
        </IconChip>
        en la barra del navegador.
      </p>
    </div>
    <div className="flex gap-3 items-start">
      <StepNumber n={2} />
      <p className="text-[15px] text-brand-black/80 leading-relaxed">
        Seleccioná
        <IconChip label="Agregar a pantalla de inicio">
          <Smartphone className="w-3.5 h-3.5 text-brand-black/60" />
        </IconChip>
      </p>
    </div>
  </div>
);

const AndroidFirefoxSteps: React.FC = () => (
  <div className="flex flex-col gap-4">
    <div className="flex gap-3 items-start">
      <StepNumber n={1} />
      <p className="text-[15px] text-brand-black/80 leading-relaxed">
        Tocá
        <IconChip>
          <MoreVertical className="w-4 h-4 text-brand-black/60" />
        </IconChip>
        en la barra del navegador.
      </p>
    </div>
    <div className="flex gap-3 items-start">
      <StepNumber n={2} />
      <p className="text-[15px] text-brand-black/80 leading-relaxed">
        Seleccioná <span className="font-semibold text-brand-black">Instalar</span>.
      </p>
    </div>
  </div>
);

const DesktopChromeSteps: React.FC = () => (
  <div className="flex flex-col gap-4">
    <div className="flex gap-3 items-start">
      <StepNumber n={1} />
      <p className="text-[15px] text-brand-black/80 leading-relaxed">
        Buscá el ícono de instalación
        <IconChip>
          <Monitor className="w-3.5 h-3.5 text-brand-black/60" />
        </IconChip>
        en la barra de direcciones (arriba a la derecha).
      </p>
    </div>
    <div className="flex gap-3 items-start">
      <StepNumber n={2} />
      <p className="text-[15px] text-brand-black/80 leading-relaxed">
        Hacé clic en <span className="font-semibold text-brand-black">Instalar</span> en el diálogo que aparece.
      </p>
    </div>
  </div>
);

const DesktopSafariSteps: React.FC = () => (
  <div className="flex flex-col gap-4">
    <div className="flex gap-3 items-start">
      <StepNumber n={1} />
      <p className="text-[15px] text-brand-black/80 leading-relaxed">
        En la barra de menú, hacé clic en <span className="font-semibold text-brand-black">Archivo</span>.
      </p>
    </div>
    <div className="flex gap-3 items-start">
      <StepNumber n={2} />
      <p className="text-[15px] text-brand-black/80 leading-relaxed">
        Seleccioná
        <IconChip label="Agregar al Dock">
          <Monitor className="w-3.5 h-3.5 text-brand-black/60" />
        </IconChip>
      </p>
    </div>
  </div>
);

const OpenInBrowserSteps: React.FC = () => (
  <div className="flex flex-col gap-4">
    <div className="flex gap-3 items-start">
      <StepNumber n={1} />
      <p className="text-[15px] text-brand-black/80 leading-relaxed">
        Tocá
        <IconChip>
          <MoreVertical className="w-4 h-4 text-brand-black/60" />
        </IconChip>
        o
        <IconChip>
          <MoreHorizontal className="w-4 h-4 text-brand-black/60" />
        </IconChip>
        en la parte superior.
      </p>
    </div>
    <div className="flex gap-3 items-start">
      <StepNumber n={2} />
      <p className="text-[15px] text-brand-black/80 leading-relaxed">
        Seleccioná
        <IconChip label="Abrir en navegador">
          <ExternalLink className="w-3.5 h-3.5 text-brand-black/60" />
        </IconChip>
      </p>
    </div>
  </div>
);

// ─── Instruction resolver ────────────────────────────────────────────────────

function getInstructions(method: InstallMethod): React.ReactNode {
  switch (method) {
    case 'ios-safari': return <IOSSafariSteps />;
    case 'ios-safari-26': return <IOSSafari26Steps />;
    case 'ios-chrome': return <IOSChromeSteps />;
    case 'android-chrome': return <AndroidChromeSteps />;
    case 'android-edge': return <AndroidEdgeSteps />;
    case 'android-firefox': return <AndroidFirefoxSteps />;
    case 'native-prompt': return <DesktopChromeSteps />;
    case 'desktop-safari': return <DesktopSafariSteps />;
    case 'open-in-browser': return <OpenInBrowserSteps />;
    default: return null;
  }
}

function getSubtitle(method: InstallMethod): string {
  if (method === 'open-in-browser') {
    return 'Primero, abrí este enlace en tu navegador';
  }
  if (method === 'native-prompt') {
    return 'Seguí estos pasos para instalar la app';
  }
  return 'Seguí estos pasos para instalar la app';
}

// ─── Sheet position helper ───────────────────────────────────────────────────
// Returns 'top' when the browser toolbar/menu the user needs to tap is at the
// bottom of the screen (so we show the sheet at the top to avoid overlap),
// and 'bottom' when the browser menu is at the top.

function getSheetPosition(method: InstallMethod): 'top' | 'bottom' {
  switch (method) {
    case 'ios-safari':       // Share button in bottom bar
    case 'ios-safari-26':    // ⋯ button in bottom bar
    case 'android-edge':     // ⋯ button in bottom bar
      return 'top';
    case 'ios-chrome':       // ⋯ in top-right
    case 'android-chrome':   // ⋮ in top-right
    case 'android-firefox':  // ⋮ in top-right
    case 'open-in-browser':  // varies, default bottom
    default:
      return 'bottom';
  }
}

// ─── Bouncing Arrow ──────────────────────────────────────────────────────────

const BouncingArrow: React.FC<{ position: 'bottom-center' | 'top-right' | 'bottom-right' | 'top-center' | 'top-left' }> = ({ position }) => {
  const positionClasses: Record<string, string> = {
    'bottom-center': 'bottom-2 left-1/2 -translate-x-1/2',
    'bottom-right': 'bottom-2 right-5',
    'top-right': 'top-2 right-5 rotate-180',
    'top-center': 'top-2 left-1/2 -translate-x-1/2 rotate-180',
    'top-left': 'top-2 left-5 rotate-180',
  };

  return (
    <div className={`absolute ${positionClasses[position]} z-50 pointer-events-none`}>
      <div className="animate-bounce">
        <svg width="28" height="40" viewBox="0 0 28 40" fill="none">
          <path
            d="M14 0L14 36M14 36L4 26M14 36L24 26"
            stroke="white"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    </div>
  );
}

function getArrowPosition(method: InstallMethod): 'bottom-center' | 'top-right' | 'bottom-right' | 'top-center' | 'top-left' | null {
  switch (method) {
    case 'ios-safari':
      return 'bottom-center';  // Share button bottom-center
    case 'ios-safari-26':
      return 'bottom-right';   // ⋯ button bottom-right in Safari 26
    case 'ios-chrome':
      return 'top-right';      // ⋯ top-right
    case 'android-chrome':
    case 'android-firefox':
      return 'top-right';      // ⋮ top-right
    case 'android-edge':
      return 'bottom-center';  // ⋯ bottom bar
    default:
      return null;
  }
}

// ─── Main Component ──────────────────────────────────────────────────────────

const AddToHomescreenPrompt: React.FC<AddToHomescreenPromptProps> = ({
  appName = 'Atlas',
  appIconUrl = '/favicon.png',
  delayMs = 3000,
  forceShow = false,
  onDismiss,
  onInstall: _onInstall,
}) => {
  const detection = useDeviceDetection();
  const [visible, setVisible] = useState(false);
  const [animatingOut, setAnimatingOut] = useState(false);
  const backdropRef = useRef<HTMLDivElement>(null);

  // Determine if we should show
  useEffect(() => {
    if (forceShow) {
      const timer = setTimeout(() => setVisible(true), 300);
      return () => clearTimeout(timer);
    }

    // Don't show if already standalone or can't install
    if (detection.isStandalone || !detection.canInstall) return;
    // Check persistence rules
    if (!shouldShowA2HSPrompt()) return;

    const timer = setTimeout(() => setVisible(true), delayMs);
    return () => clearTimeout(timer);
  }, [forceShow, detection.isStandalone, detection.canInstall, delayMs]);

  const handleDismiss = useCallback(() => {
    setAnimatingOut(true);
    setTimeout(() => {
      setVisible(false);
      setAnimatingOut(false);
      if (!forceShow) {
        setA2HSDismissed();
      }
      onDismiss?.();
    }, 300);
  }, [forceShow, onDismiss]);

  // Click outside to close (on backdrop)
  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === backdropRef.current) {
      handleDismiss();
    }
  }, [handleDismiss]);

  if (!visible) return null;

  // Render via portal so the overlay is always above nav bars / stacking contexts
  const portalRoot = document.body;

  const { installMethod, device } = detection;
  const isMobile = device === 'ios' || device === 'android';
  const instructions = getInstructions(installMethod);
  const subtitle = getSubtitle(installMethod);
  const arrowPos = getArrowPosition(installMethod);

  // ── Desktop — centered modal with steps (Chrome, Edge, Safari) ────────

  if (!isMobile) {
    return createPortal(
      <div
        ref={backdropRef}
        onClick={handleBackdropClick}
        className={`fixed inset-0 z-[9999] flex items-center justify-center bg-brand-black/50 backdrop-blur-sm transition-opacity duration-300 ${
          animatingOut ? 'opacity-0' : 'opacity-100'
        }`}
      >
        <div
          className={`bg-white rounded-lg shadow-xl max-w-[400px] w-[90%] overflow-hidden transition-all duration-300 ${
            animatingOut ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
          }`}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 pt-5 pb-3">
            <div className="flex items-center gap-3">
              <img
                src={appIconUrl}
                alt={appName}
                className="w-10 h-10 rounded-sm shadow-sm"
              />
              <div>
                <h2 className="text-[15px] font-semibold text-brand-black">
                  Instalar {appName}
                </h2>
                <p className="text-[13px] text-brand-black/50">{subtitle}</p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="w-8 h-8 flex items-center justify-center rounded-sm text-brand-black/40 hover:bg-neutral-100 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Steps */}
          <div className="px-5 pb-5 pt-2">
            {instructions}
          </div>

          {/* Blurb */}
          <div className="mx-5 mb-5 px-3 py-2.5 bg-brand-primary/[0.04] rounded-sm border border-brand-primary/10">
            <p className="text-[13px] text-brand-black/50 leading-relaxed">
              Se agregará un acceso directo para que puedas abrir {appName} como una app.
            </p>
          </div>
        </div>
      </div>,
      portalRoot
    );
  }

  // ── Mobile — top or bottom sheet depending on browser toolbar position ──

  const sheetPosition = getSheetPosition(installMethod);
  const isTopSheet = sheetPosition === 'top';

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        ref={backdropRef}
        onClick={handleBackdropClick}
        className={`fixed inset-0 z-[9999] bg-brand-black/50 backdrop-blur-sm transition-opacity duration-300 ${
          animatingOut ? 'opacity-0' : 'opacity-100'
        }`}
      />

      {/* Bouncing arrow */}
      {arrowPos && !animatingOut && <BouncingArrow position={arrowPos} />}

      {/* Sheet */}
      <div
        className={`fixed left-0 right-0 z-[10000] transition-transform duration-300 ease-out ${
          isTopSheet
            ? `top-0 ${animatingOut ? '-translate-y-full' : 'translate-y-0'}`
            : `bottom-0 ${animatingOut ? 'translate-y-full' : 'translate-y-0'}`
        }`}
      >
        <div
          className={`bg-white overflow-hidden ${
            isTopSheet ? 'rounded-b-lg' : 'rounded-t-lg'
          }`}
          style={isTopSheet
            ? { paddingTop: 'env(safe-area-inset-top, 0px)' }
            : { paddingBottom: 'env(safe-area-inset-bottom, 0px)' }
          }
        >
          {/* Handle bar — top for bottom-sheet, bottom for top-sheet */}
          {!isTopSheet && (
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-9 h-1 rounded-full bg-brand-black/10" />
            </div>
          )}

          {/* Header row */}
          <div className="flex items-center justify-between px-5 pb-3 pt-1">
            <div className="flex items-center gap-3">
              <img
                src={appIconUrl}
                alt={appName}
                className="w-12 h-12 rounded-md shadow-sm"
              />
              <div>
                <h2 className="text-[17px] font-semibold text-brand-black">
                  Instalar {appName}
                </h2>
                <p className="text-[13px] text-brand-black/50">
                  {subtitle}
                </p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-brand-black/[0.06] text-brand-black/40 active:bg-brand-black/10 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Divider */}
          <div className="mx-5 h-px bg-brand-black/[0.06]" />

          {/* Steps */}
          <div className="px-5 py-4">
            {instructions}
          </div>

          {/* Blurb */}
          <div className="mx-5 mb-5 px-3 py-2.5 bg-brand-primary/[0.04] rounded-md border border-brand-primary/10">
            <p className="text-[13px] text-brand-black/50 leading-relaxed">
              Se agregará un ícono a tu pantalla de inicio para acceder rápidamente a {appName}.
            </p>
          </div>

          {/* Handle bar at bottom for top-sheet */}
          {isTopSheet && (
            <div className="flex justify-center pb-3 pt-1">
              <div className="w-9 h-1 rounded-full bg-brand-black/10" />
            </div>
          )}
        </div>
      </div>
    </>,
    portalRoot
  );
};

export default AddToHomescreenPrompt;
